import MojitoCore from 'mojito/core';
import { useMemo } from 'react';
import { isEmpty } from 'mojito/utils';
import Image from 'presentation/components/image/index.js';

const uiStyle = MojitoCore.Presentation.uiStyle;
const EMPTY_FILTER_ITEMS = [];
export const ALL_SPORTS_ITEM_ID = 'all';

/**
 * Contains custom hooks building the menu of {@link Mojito.Presentation.Components.SportsFilter}.
 *
 * @name hooks
 * @memberof Mojito.Presentation.Components.SportsFilter
 */

/**
 * Builds sport filter menu items.
 *
 * @function useSportFilterItems
 *
 * @param {Array<string>} sportIds - Sports that can be selected. An empty list hides the filter.
 * @param {object} mojitoTools - Mojito tools of the SportsFilter component.
 *
 * @returns {Array<object>} Menu items, starting with the item that keeps the content of every sport.
 *
 * @memberof Mojito.Presentation.Components.SportsFilter.hooks
 */
export const useSportFilterItems = (sportIds, mojitoTools) =>
    useMemo(() => {
        if (isEmpty(sportIds)) {
            return EMPTY_FILTER_ITEMS;
        }
        const { config, appContext, stringResolver } = mojitoTools;
        const { sportIcons } = config;
        const allSportsText = stringResolver.resolveString(config.sportFilterAllSportsTextKey);
        return [
            {
                id: ALL_SPORTS_ITEM_ID,
                data: allSportsText,
                iconRenderer: isSelected => (
                    <Image
                        src={config.allSportsIcon}
                        alt={allSportsText}
                        class="ta-ItemIcon"
                        config={
                            isSelected ? config.selectedAllSportsItemIcon : config.allSportsItemIcon
                        }
                    />
                ),
            },
            ...sportIds.map(sportId => ({
                id: sportId,
                data: appContext.sportName(sportId),
                icon: sportIcons[sportId] || sportIcons.defaultIcon,
                altText: appContext.sportName(sportId),
            })),
        ];
    }, [sportIds, mojitoTools]);

/**
 * Applies the accent colours of the SportsFilter config to the selected item of its menu.
 * The selected item keeps the colours of its config when the selected sport has no accent colour.
 *
 * @function useSportFilterMenuConfig
 *
 * @param {object} menuConfig - MenuRow config of the sport filter.
 * @param {string} [selectedSportId] - Selected sport, all sports when not given.
 * @param {object} config - SportsFilter config holding `sportFilterAccentColors`, `sportFilterDefaultAccentColor` and `sportFilterAllSportsBackgroundColor`.
 *
 * @returns {object} MenuRow config.
 * @memberof Mojito.Presentation.Components.SportsFilter.hooks
 */
export const useSportFilterMenuConfig = (menuConfig, selectedSportId, config) => {
    const isAllSports = !selectedSportId;
    const accentColor = isAllSports
        ? config.sportFilterDefaultAccentColor
        : config.sportFilterAccentColors?.[selectedSportId];
    const backgroundColor = isAllSports ? config.sportFilterAllSportsBackgroundColor : undefined;
    return useMemo(() => {
        if (!accentColor && !backgroundColor) {
            return menuConfig;
        }
        const selectedItemStyle = {
            ...(backgroundColor && { backgroundColor }),
            ...(accentColor && { borderColor: accentColor }),
        };
        return uiStyle.merge(menuConfig, {
            item: { selected: { style: { base: selectedItemStyle } } },
            ...(accentColor && { selectedItemIcon: { style: { fill: accentColor } } }),
        });
    }, [menuConfig, accentColor, backgroundColor]);
};
